/*
 * Copyright (c) 2022. American Express Company
 */

import React, { useCallback } from 'react';

import PlanService, { InsightElements } from 'src/services/plan.service';
import NavigationService from 'src/services/navigation.service';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Typography from 'src/components/typography/Typography';
import Card from 'src/components/atomic/atoms/card/Card';
import Divider from 'src/components/atomic/atoms/divider/Divider';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import InsightsStatsBox from 'src/components/atomic/molecules/insightsStatsBox/InsightsStatsBox';
import NewTransactionsRecap from 'src/components/atomic/molecules/newTransactionsRecap/NewTransactionsRecap';
import Suggestion from 'src/components/atomic/molecules/suggestion/Suggestion';
import SavingsRecap from 'src/components/atomic/molecules/savingsRecap/SavingsRecap';
import BudgetRecap from 'src/components/atomic/molecules/budgetRecap/BudgetRecap';
import PlanRecap from 'src/components/atomic/molecules/planRecap/PlanRecap';
import PlanActions from 'src/components/atomic/molecules/planActions/PlanActions';
import GetStarted from 'src/components/atomic/molecules/getStarted/GetStarted';
import SavingsGoals from 'src/components/atomic/molecules/savingsGoals/SavingsGoals';
import { BudgetModel } from 'src/types/budget.model';
import { BudgetRecapVariant } from 'src/types/view-models/BudgetRecapVariant.enum';
import { InsightsViewViewModel } from 'src/types/view-models/insights-view.view.model';

import util from './insightsView.util';
import styles from './insightsView.module.scss';
import { CardModel } from 'src/types/card.model';
import Selectors from 'src/utils/selectors';

export default function InsightsView({
    user,
    stats,
    budgets,
    summary,
    savingsGoalsCards,
    history,
    handleBack,
    handleUpdateUserAttribute,
    handleOpenIncomeSummary,
    handleOpenExpenses,
    handleReviewExpenses,
    handleOpenTransactions,
    handleOpenTransactionWithId,
    handleOpenCard,
    handleOpenLinkedCard
}: InsightsViewViewModel): JSX.Element {
    const plan: InsightElements = PlanService.getInsightsElements(user, budgets);

    const {
        netWorthDifference,
        savingsDifference,
        investmentsDifference,
        savingsPercentDifference
    }: {
        netWorthDifference: number;
        savingsDifference: number;
        investmentsDifference: number;
        savingsPercentDifference: number;
    } = util.processStats(stats, budgets, plan);

    const goTo = useCallback(
        (nextPath: string, label?: string): void => {
            void NavigationService.goTo(history, nextPath, label);
        },
        [history]
    );

    const renderBudgetRecap = (): JSX.Element => {
        let budgetRecap: JSX.Element = (
            <BudgetRecap
                viewBudgetsOnClick={() => util.viewBudgetsOnClick(history)}
                variant={BudgetRecapVariant.UNDER_BUDGET}
                data-cy={Selectors.PlanAhead.BUDGET_RECAP_UNDER}
            />
        );
        if (summary?.overBudget)
            budgetRecap = (
                <BudgetRecap
                    budgetsLength={summary.overBudget}
                    viewBudgetsOnClick={() => util.viewBudgetsOnClick(history)}
                    variant={BudgetRecapVariant.OVERSPENDING}
                    data-cy={Selectors.PlanAhead.BUDGET_RECAP_OVER}
                />
            );
        else if (summary?.warnBudget)
            budgetRecap = (
                <BudgetRecap
                    budgetsLength={summary.warnBudget}
                    viewBudgetsOnClick={() => util.viewBudgetsOnClick(history)}
                    variant={BudgetRecapVariant.ALMOST_OVERSPENDING}
                    data-cy={Selectors.PlanAhead.BUDGET_RECAP_ALMOST_OVER}
                />
            );
        return budgetRecap;
    };

    return (
        <WideView
            title="Plan Insights"
            data-cy={Selectors.PlanAhead.INSIGHTS_MAIN_VIEW}
            history={history}
            handleBack={handleBack}
            hasBackgroundBanner
            showFooter
        >
            <div className={styles.insightsView}>
                <div className={styles.left}>
                    <PlanRecap userStatus={user.status} />
                    <Card>
                        <Typography component="h2" variant={TypographyVariants.HEADING_XS}>
                            Since your last login
                        </Typography>
                        <Divider />
                        <SavingsRecap
                            currentPercent={stats.savingsOverview.currentPercent}
                            percentDifference={savingsPercentDifference}
                            goTo={goTo}
                            showWarning
                        />
                        {budgets.length !== 0 && renderBudgetRecap()}
                        <InsightsStatsBox
                            netWorthDifference={netWorthDifference}
                            netWorthPercentChange={stats.netWorth.percentChange}
                            netWorthGlobalPercentChange={stats.netWorth.globalPercentChange}
                            savingsDifference={savingsDifference}
                            savingsPercentChange={stats.savingsAccounts.percentChange}
                            savingsGlobalPercentChange={stats.savingsAccounts.globalPercentChange}
                            investmentsDifference={investmentsDifference}
                            investmentsPercentChange={stats.investmentAccounts.percentChange}
                            investmentsGlobalPercentChange={stats.investmentAccounts.globalPercentChange}
                        />
                        <NewTransactionsRecap
                            topDeposit={stats.topDeposit}
                            topExpense={stats.topExpense}
                            newTransactions={stats.newTransactions}
                            handleOpenIncomeSummary={handleOpenIncomeSummary}
                            handleOpenExpenses={handleOpenExpenses}
                            handleOpenTransactions={handleOpenTransactions}
                            handleOpenTransactionWithId={handleOpenTransactionWithId}
                        />
                    </Card>
                    <SavingsGoals className={styles.hideDesktop} goTo={goTo} savingsGoalsCards={savingsGoalsCards} />
                    {plan.addAccount && <Suggestion variant="connectAccounts" />}
                    {(plan.addLifeEvent || plan.viewExpenseProjection || !budgets.length) && (
                        <GetStarted
                            user={user}
                            budgets={budgets}
                            plan={plan}
                            addLifeEventOnClick={async () =>
                                await util.addLifeEventOnClick(history, handleUpdateUserAttribute)
                            }
                            viewProjectionsOnClick={async () =>
                                await util.viewProjectionsOnClick(user, history, handleUpdateUserAttribute)
                            }
                            createBudgetsOnClick={() => util.createBudgetsOnClick(history)}
                        />
                    )}
                    <PlanActions
                        user={user}
                        viewReviewTransactionsOnClick={() => util.viewReviewTransactionsOnClick(handleReviewExpenses)}
                        viewBudgetTransactionsOnClick={(budget: BudgetModel) =>
                            util.viewBudgetTransactionsOnClick(budget, user.rawCashFlow, handleOpenTransactions)
                        }
                        viewCardOnClick={(card: CardModel) => handleOpenCard(card)}
                        viewLinkedCardOnClick={(card: CardModel) => handleOpenLinkedCard(card)}
                        goTo={goTo}
                        plan={plan}
                    />
                </div>
                <div className={styles.right}>
                    <SavingsGoals goTo={goTo} savingsGoalsCards={savingsGoalsCards} />
                </div>
            </div>
        </WideView>
    );
}
